/**
 * Copyright (c) 2026, CYBRA Corp. All rights reserved.
 */
import {Injectable} from '@angular/core';
import {Router} from "@angular/router";

import {AuthenticationService} from './authentication.service';
import {BaseService} from "./base-service";

import {ObjectCategory} from '../models/object-category';
import {ObjectDefinition} from '../models/object-definition';
import {ObjectInstance} from '../models/object-instance';
import {Point} from "../models/point";
import {PropertyPatch} from "../models/property-patch";
import {TagInstance} from '../models/tag-instance';
import { PagedResults } from 'app/models/paged-results';
import { RestService } from './rest-service';

import { Url_Base, Url_ObjectProperties_MainDisplayForCategory } from 'app/rest-constants';
import { FilterOption, buildFilterParams } from '../models/filter-option';
import { ObjectProperty } from 'app/models/object-property';
import { InstanceData } from 'app/models/instance-data';
import { PagingControls } from 'app/models/paging-controls';
import { PropertySearchDto } from 'app/models/property-search-dto';

@Injectable()
export class ObjectService extends BaseService {

  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private mRestService: RestService,
  ) {
    super(router, authenticationService);
  }

  private getUrl(): string {
    return `${Url_Base}/objects/`;
  }


  getAllObjectsForCategory(objectCategory: ObjectCategory): Promise<ObjectDefinition[]> {
    const callUrl = this.getUrl() + 'type/' + objectCategory;

    return this.mRestService.get<ObjectDefinition[]>(callUrl);
  }

  getAllObjectsForCategoryPaging(objectCategory: ObjectCategory, start: number, size: number, order: String, filterString: String): Promise<PagedResults<ObjectDefinition>> {
    const callUrl = this.getUrl() + 'type/' + objectCategory + '/paging?start=' + start + "&size=" + size + "&order=ASC&filterColumn=name&filterString=" + filterString;

    return this.mRestService.get<PagedResults<ObjectDefinition>>(callUrl);
  }

  getAllAssetObjects(): Promise<ObjectDefinition[]> {
    return this.getAllObjectsForCategory(ObjectCategory.ASSET);
  }

  getAllPeopleObjects(): Promise<ObjectDefinition[]> {
    return this.getAllObjectsForCategory(ObjectCategory.PEOPLE);
  }

  getNumberObjects(objectType: String): Promise<Number> {
    const callUrl = this.getUrl() + 'type/' + objectType + '/count';
    return this.mRestService.get<Number>(callUrl);
  }

  getAllDeviceObjects(): Promise<ObjectDefinition[]> {
    return this.getAllObjectsForCategory(ObjectCategory.DEVICE);
  }

  getAllRfidEncodingSchemeObjects(): Promise<ObjectDefinition[]> {
    return this.getAllObjectsForCategory(ObjectCategory.PASSIVE);
  }

  getObjectForId(id: number): Promise<ObjectDefinition> {
    const callUrl = this.getUrl() + `${id}`;
    return this.mRestService.get<ObjectDefinition>(callUrl);
  }

  getObjectForUuid(uuid: string): Promise<ObjectDefinition> {
    const callUrl = `${this.getUrl()}uuid/${uuid}`;
    return this.mRestService.get<ObjectDefinition>(callUrl);
  }

  createObject(objectDefinition: ObjectDefinition): Promise<ObjectDefinition> {
    const callUrl = this.getUrl();
    return this.mRestService.post<ObjectDefinition>(callUrl, objectDefinition);
  }

  updateObject(objectDefinition: ObjectDefinition): Promise<ObjectDefinition> {
    const callUrl = this.getUrl();
    return this.mRestService.put<ObjectDefinition>(callUrl, objectDefinition);
  }

  deleteObjects(objectIds: number[]): Promise<void> {
    const callUrl = this.getUrl();
    return this.mRestService.delete(callUrl, objectIds)
      .then(response => {
        return;
      });
  }










  getAllObjectInstances(): Promise<ObjectInstance[]> {
    const callUrl = this.getUrl() + `instances`;
    return this.mRestService.get<ObjectInstance[]>(callUrl);
  }

  getAllObjectInstancesForCategory(objectCategory: ObjectCategory): Promise<ObjectInstance[]> {
    const callUrl = this.getUrl() + 'instances/type/' + objectCategory;
    return this.mRestService.get<ObjectInstance[]>(callUrl);
  }


  /**
   * @param objectCategory - Object Category of the Instances to return (Assets / People / Devices, etc)
   * @param zoneId - ID of the Zone the returned Instances are currently in
   * @returns An array of all the ObjectInstances that match the parameters.
   */
  getAllObjectInstancesForCategoryInZone(objectCategory: ObjectCategory, zoneId: number) {
    const callUrl = `${this.getUrl()}instances/type/${objectCategory}?zoneId=${zoneId}`;
    return this.mRestService.get<ObjectInstance[]>(callUrl);
  }

  getAllObjectInstancesForCategoryInZonePaged(objectCategory: ObjectCategory, zoneId: number, start: number, size: number, order: String, filterString: String, filterColumn: String): Promise<PagedResults<ObjectInstance>> {
  const callUrl = this.getUrl() + 'instances/type/' + objectCategory + '/paging-new'
    + '?zoneId=' + zoneId
    + '&start=' + start
    + '&size=' + size
    + '&order=' + order
    + '&filterColumn=' + filterColumn
    + '&filterString=' + filterString;
  return this.mRestService.get<PagedResults<ObjectInstance>>(callUrl);
}

  /**
   * @param objectCategory - Object Category of the Instances to return (Assets / People / Devices, etc)
   * @param locationId - ID of the Location the returned Instances are currently in
   * @returns An array of all the ObjectInstances that match the parameters.
   */
  getAllObjectInstancesForCategoryInLocation(objectCategory: ObjectCategory, locationId: number) {
    const callUrl = `${this.getUrl()}instances/type/${objectCategory}?locationId=${locationId}`;
    return this.mRestService.get<ObjectInstance[]>(callUrl);
  }

getAllObjectInstancesForCategoryInLocationPaged(objectCategory: ObjectCategory, locationId: number, start: number, size: number, order: String, filterString: String, filterColumn: String): Promise<PagedResults<ObjectInstance>> {
  const callUrl = this.getUrl() + 'instances/type/' + objectCategory + '/paging-new'
    + '?locationId=' + locationId
    + '&start=' + start
    + '&size=' + size
    + '&order=' + order
    + '&filterColumn=' + filterColumn
    + '&filterString=' + filterString;
  return this.mRestService.get<PagedResults<ObjectInstance>>(callUrl);
}

  getAllObjectInstancesForCategoryPaging(objectCategory: ObjectCategory, start: number, size: number, order: String, filterString: String): Promise<PagedResults<ObjectInstance>> {
    const callUrl = this.getUrl() + 'instances/type/' + objectCategory + '/paging?start=' + start + "&size=" + size + "&order=ASC&filterColumn=name&filterString=" + filterString;
    return this.mRestService.get<PagedResults<ObjectInstance>>(callUrl);
  }

  // Pages Object Instances for a category applying multiple filters at once (Name + Type), AND'd by the backend.
  getAllObjectInstancesForCategoryPagingWithFilters(objectCategory: ObjectCategory, start: number, size: number, order: string, filters: FilterOption[]): Promise<PagedResults<ObjectInstance>> {
    const callUrl = this.getUrl() + 'instances/type/' + objectCategory + '/paging?start=' + start + "&size=" + size + "&order=" + order + buildFilterParams(filters);
    return this.mRestService.get<PagedResults<ObjectInstance>>(callUrl);
  }

  /**
   * Calls the Property Search Paged REST API. 
   * Searches for Object Instances with specified properties with a value like a given value. 
   */
  propertySearchInstances(propertyUuid: string, dataValue: string, pagingControls?: PagingControls): Promise<PagedResults<ObjectInstance>> {
    const callUrl = `${this.getUrl()}instances/property-search` + (pagingControls != null? `?${pagingControls.toUrlParamString()}` : "");
    const searchDto= { property_uuid: propertyUuid, data_value: dataValue} as PropertySearchDto;
    return this.mRestService.post<PagedResults<ObjectInstance>>(callUrl, searchDto);
  }

  getAllAssetObjectInstances(): Promise<ObjectInstance[]> {
    return this.getAllObjectInstancesForCategory(ObjectCategory.ASSET);
  }
  getAllPeopleObjectInstances(): Promise<ObjectInstance[]> {
    return this.getAllObjectInstancesForCategory(ObjectCategory.PEOPLE);
  }

  getAllPeopleObjectInstancesPaged(start: number, size: number, order: String, filterString: String): Promise<PagedResults<ObjectInstance>> {
    return this.getAllObjectInstancesForCategoryPaging(ObjectCategory.PEOPLE, start, size, order, filterString);
  }

  getAllAssetObjectInstancesPaged(start: number, size: number, order: String, filterString: String): Promise<PagedResults<ObjectInstance>> {
    return this.getAllObjectInstancesForCategoryPaging(ObjectCategory.ASSET, start, size, order, filterString);
  }

  getNumberObjectInstances(objectInstanceType: String): Promise<Number> {
    const callUrl = this.getUrl() + 'instances/type/' + objectInstanceType + '/count';
    return this.mRestService.get<Number>(callUrl);
  }

  getAllDeviceObjectInstances(): Promise<ObjectInstance[]> {
    return this.getAllObjectInstancesForCategory(ObjectCategory.DEVICE);
  }

  getAllRfidEncodingSchemeObjectInstances(): Promise<ObjectInstance[]> {
    return this.getAllObjectInstancesForCategory(ObjectCategory.PASSIVE);
  }

  getObjectInstanceForId(objectInstanceId: number): Promise<ObjectInstance> {
    const callUrl = this.getUrl() + `instances/${objectInstanceId}`;
    return this.mRestService.get<ObjectInstance>(callUrl);
  }

getObjectInstancesFiltered(start: number, size: number, order: string, filterString: string): Promise<ObjectInstance[]> {
  const url = this.getUrl() + `instances/filtered?start=` + start + "&size=" + size + "&order=" + order + "&filterColumn=name&filterString=" + filterString;
  return this.mRestService.get<ObjectInstance[]>(url);
}

  createObjectInstance(objectInstance: ObjectInstance): Promise<ObjectInstance> {
    const callUrl = this.getUrl() + `instances`;
    return this.mRestService.post<ObjectInstance>(callUrl, objectInstance);
  }

  updateObjectInstance(objectInstance: ObjectInstance): Promise<ObjectInstance> {
    const callUrl = this.getUrl() + `${objectInstance.object.id}/instances`;
    return this.mRestService.put<ObjectInstance>(callUrl, objectInstance);
  }

  /**
   * Calls the REST API that update's the corresponding ObjectInstance's Property Data only.
   * This returns the "updated" version of the ObjectInstance after successfully saving the Data.
   * This is used in the Locate Page for the "Edit Data" Action on Assets.
   */
  updateObjectInstanceData(objectInstanceId: number, data: InstanceData[]): Promise<ObjectInstance> {
    const callUrl = this.getUrl() + `instances/${objectInstanceId}/update-data`;
    return this.mRestService.post<ObjectInstance>(callUrl, data);
  }

  setObjectInstancePosition(objectInstanceId: number, locationId: number, position: Point): Promise<ObjectInstance> {
    const callUrl = this.getUrl() + `instances/${objectInstanceId}/set-position`;
    const dto = { location_id: locationId, position: position };
    return this.mRestService.put<ObjectInstance>(callUrl, dto);
  }

  deleteObjectInstances(objectInstanceIds: number[]): Promise<void> {
    const callUrl = this.getUrl() + `instances`;
    return this.mRestService.delete(callUrl, objectInstanceIds);
  }

  getTagsForObjectInstance(objectInstanceId: number): Promise<TagInstance> {
    const callUrl = this.getUrl() + `instances/${objectInstanceId}/assigned_tag`;
    return this.mRestService.get<TagInstance>(callUrl);
  }

  getAssignedTagsForObjectInstance(objectInstanceId: number): Promise<TagInstance[]> {
    const callUrl = this.getUrl() + `instances/${objectInstanceId}/assigned_tags`;
    return this.mRestService.get<TagInstance[]>(callUrl);
  }

  assignTagsToObjectInstance(objectInstanceId: number, tag: TagInstance): Promise<void> {
    const callUrl = this.getUrl() + `instances/${objectInstanceId}/assigned_tags`;
    return this.mRestService.put(callUrl, tag);
  }

  setAssignedTagsForObjectInstance(objectInstanceId: number, tags: TagInstance[]): Promise<void> {
    const callUrl = this.getUrl() + `instances/${objectInstanceId}/assigned_tags`;
    return this.mRestService.post(callUrl, tags);
  }

  unassignAllTagsFromObjectInstance(objectInstanceId: number): Promise<void> {
    const callUrl = this.getUrl() + `instances/${objectInstanceId}/assigned_tag`;
    return this.mRestService.delete(callUrl);
  }

  executeFunction(objectInstanceId: number, functionId: number, propertyPatches: PropertyPatch[], responseType?: string): Promise<any> {
    const callUrl = this.getUrl() + `instances/${objectInstanceId}/execute-function?function_id=${functionId}`;
    return this.mRestService.post(callUrl, propertyPatches, responseType);
  }

  uploadTypes(formData: FormData): Promise<void> {
    const callUrl = this.getUrl() + 'types/upload';
    return this.mRestService.upload(callUrl, formData);
  }

  downloadTypes(): Promise<Blob> {
    const callUrl = this.getUrl() + 'types/download';

    return this.mRestService.get(callUrl)
      .then(response => response['_body'] as Blob);
  }

  uploadInstances(formData: FormData): Promise<void> {
    const callUrl = this.getUrl() + 'instances/upload';
    return this.mRestService.upload(callUrl, formData);
  }

  downloadInstances(instanceId: number): Promise<Blob> {
    const callUrl = this.getUrl() + `instances/download/${instanceId}`;

    return this.mRestService.get(callUrl)
      .then(response => response['_body'] as Blob);
  }

  async getAllPropertiesForMainDisplayAndCategory(category: ObjectCategory): Promise<ObjectProperty[]> {
    return this.mRestService.get<ObjectProperty[]>(Url_ObjectProperties_MainDisplayForCategory(category));
  }

  async getObjectTypesForReference(
  start: number,
  size: number,
  order: string,
  filterString: string
): Promise<ObjectDefinition[]> {

  const [assets, people, devices] = await Promise.all([
    this.getAllObjectsForCategoryPaging(ObjectCategory.ASSET, start, size, order, filterString),
    this.getAllObjectsForCategoryPaging(ObjectCategory.PEOPLE, start, size, order, filterString),
    this.getAllObjectsForCategoryPaging(ObjectCategory.DEVICE, start, size, order, filterString)
  ]);

  return [
    ...(assets?.data || []),
    ...(people?.data || []),
    ...(devices?.data || [])
  ];
}

  getPropertiesForObjectType(objectId: number): Promise<ObjectProperty[]> {
  const callUrl = `${this.getUrl()}${objectId}/properties`;
  return this.mRestService.get<ObjectProperty[]>(callUrl);
}
}
